import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Target, Palette, Code, Fingerprint, Bot, Server, 
    TrendingUp, Globe, Brain, Workflow, Grid, Zap, 
    Megaphone, BarChart3, Share2,
    X, ArrowUpRight, CheckCircle2, Search, Filter,
    LayoutDashboard, Lock, ShieldCheck, Smartphone
} from 'lucide-react';

interface Capability {
  id: number;
  number: string;
  title: string;
  category: 'Engineering' | 'AI & Automation' | 'Design & UX' | 'Strategy & Growth';
  icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>;
  desc: string;
  impact: string;
  deliverables: string[];
  techStack: string[];
  architecturalApproach: string;
}

const capabilitiesData: Capability[] = [
  {
    id: 1,
    number: "01",
    title: "Product Strategy",
    category: "Strategy & Growth",
    icon: Target,
    desc: "Uncovering deep business objectives and transforming fuzzy requirements into concrete, actionable product roadmaps.",
    impact: "Clarity from Day One",
    deliverables: ["KPI & Milestone Mapping", "Competitive Moat Analysis", "Technical Feasibility Study", "MVP Scope Definition"],
    techStack: ["Product Roadmapping", "User Story Mapping", "Unit Economics", "Data Modeling"],
    architecturalApproach: "Align technical investments directly with commercial revenue drivers, preventing premature optimization and feature bloat."
  },
  {
    id: 2,
    number: "02",
    title: "UX/UI Design",
    category: "Design & UX",
    icon: Palette,
    desc: "Crafting intuitive, high-density interfaces for complex applications — from SaaS portals to mission-critical healthcare systems.",
    impact: "Frictionless Workflows",
    deliverables: ["High-Fidelity Figma Systems", "Interactive Prototypes", "Micro-Interactions & Motion", "Information Architecture"],
    techStack: ["Figma", "Design Tokens", "Framer Motion", "Tailwind CSS"],
    architecturalApproach: "Design from user cognitive load inward: prioritize clear visual hierarchies, predictable mental models, and rapid task completion."
  },
  {
    id: 3,
    number: "03",
    title: "Full Stack Engineering",
    category: "Engineering",
    icon: Code,
    desc: "End-to-end software development spanning type-safe frontends, resilient backend services, and scalable cloud data layers.",
    impact: "Production-Grade Velocity",
    deliverables: ["Modular React/Next.js Apps", "REST & GraphQL APIs", "Relational Database Schemas", "Zero-Downtime CI/CD"],
    techStack: ["TypeScript", "React", "Node.js", "PostgreSQL", "Docker"],
    architecturalApproach: "Enforce strict end-to-end typing, clean separation of concerns, and defensive data access patterns across all layers."
  },
  {
    id: 4,
    number: "04",
    title: "Branding & Identity",
    category: "Design & UX",
    icon: Fingerprint,
    desc: "Building cohesive visual systems from scratch with bespoke typography, disciplined color architectures, and comprehensive brand guidelines.",
    impact: "Distinct Market Presence",
    deliverables: ["Visual Identity Systems", "Custom Typography & Palettes", "Brand Guideline Books", "Digital Asset Libraries"],
    techStack: ["SVG Mastery", "Color Science", "Type Hierarchy", "Vector Graphics"],
    architecturalApproach: "Ensure the visual identity scales consistently across micro mobile screens, marketing touchpoints, and large display media."
  },
  {
    id: 5,
    number: "05",
    title: "Meta & Social Ads",
    category: "Strategy & Growth",
    icon: Megaphone,
    desc: "Scaling high-ROAS paid acquisition campaigns across Meta (Facebook & Instagram Ads), TikTok, and LinkedIn with algorithmic audience targeting.",
    impact: "3x–5x Targeted ROAS",
    deliverables: ["Meta Ads Manager Campaign Setup", "FB & Instagram Creative Strategy", "Conversions API (CAPI) & Pixel Setup", "Lookalike & Retargeting Funnels"],
    techStack: ["Meta Ads Manager", "Instagram Ads", "Conversions API (CAPI)", "Meta Pixel", "Creative Testing Matrix"],
    architecturalApproach: "Build full-funnel architectures connecting top-of-funnel discovery to high-intent retargeting, using server-side Conversions API to eliminate signal loss."
  },
  {
    id: 6,
    number: "06",
    title: "Google Ads & PPC Growth",
    category: "Strategy & Growth",
    icon: BarChart3,
    desc: "Capturing high-intent buyers across Google Search, Shopping, Performance Max (PMax), Display, and YouTube with precision bidding strategies.",
    impact: "High-Intent Customer Acquisition",
    deliverables: ["Google Search & PMax Campaigns", "Keyword & Negative Match Architecture", "Server-Side Conversion Tracking", "Landing Page Ad Congruence"],
    techStack: ["Google Ads", "Performance Max (PMax)", "Google Tag Manager", "GA4 / ROAS Tracking", "Search Console"],
    architecturalApproach: "Structure single-theme ad groups (STAGs) and multi-asset PMax groups with value-based bidding algorithms to maximize revenue per click."
  },
  {
    id: 7,
    number: "07",
    title: "AI Automation",
    category: "AI & Automation",
    icon: Bot,
    desc: "Deploying intelligent AI pipelines that eliminate repetitive manual operations, handle client routing, and synthesize vast document sets.",
    impact: "10x Operational Speed",
    deliverables: ["LLM Task Orchestration", "CRM Lead Auto-Classification", "Document Analysis Engines", "Automated Knowledge Assistants"],
    techStack: ["Gemini API", "OpenAI", "LangChain", "Vector Embeddings"],
    architecturalApproach: "Implement deterministic guardrails and fallback mechanisms around probabilistic LLMs to guarantee dependable business execution."
  },
  {
    id: 8,
    number: "08",
    title: "SaaS Architecture",
    category: "Engineering",
    icon: Server,
    desc: "Constructing the robust multi-tenant foundations behind software platforms: session authentication, granular RBAC, and rate limits.",
    impact: "Enterprise Readiness",
    deliverables: ["Multi-Tenant Data Isolation", "JWT & OAuth Security", "Granular Role-Based Access", "Stripe Subscription Engines"],
    techStack: ["PostgreSQL", "Redis", "Cloud Run", "Stripe Billing"],
    architecturalApproach: "Isolate tenant contexts at the database level and decouple authentication from business logic for audit-ready compliance."
  },
  {
    id: 9,
    number: "09",
    title: "Social Media Marketing",
    category: "Strategy & Growth",
    icon: Share2,
    desc: "End-to-end organic & paid social strategy, viral content engines, community engagement, and influencer collaboration frameworks.",
    impact: "Compounding Community Reach",
    deliverables: ["Cross-Platform Content Strategy", "Video / Reel / Post Creative Production", "Community Engagement Playbooks", "Omnichannel Growth Calendars"],
    techStack: ["Instagram", "Facebook", "TikTok", "LinkedIn", "Buffer / Later", "Canva / Figma"],
    architecturalApproach: "Engineer distribution feedback loops where organic audience insights directly inform paid ad creative iterations."
  },
  {
    id: 10,
    number: "10",
    title: "Platform Development",
    category: "Engineering",
    icon: Globe,
    desc: "Engineering custom web applications from the ground up — booking engines, marketplace systems, community hubs, and internal tooling.",
    impact: "Bespoke Scalability",
    deliverables: ["Real-time Collaboration", "High-Throughput Search", "Media Processing Pipelines", "Admin Management Portals"],
    techStack: ["Next.js", "WebSockets", "Cloud Storage", "Tailwind CSS"],
    architecturalApproach: "Design modular services capable of horizontal scaling with automated queue processing and resilient caching tiers."
  },
  {
    id: 11,
    number: "11",
    title: "Agentic AI",
    category: "AI & Automation",
    icon: Brain,
    desc: "Building autonomous agents equipped with tool-calling capabilities, semantic retrieval systems, and contextual memory stores.",
    impact: "Autonomous Reasoning",
    deliverables: ["Multi-Step Agent Workflows", "Dynamic Tool-Calling APIs", "RAG Knowledge Bases", "Context Management Strategies"],
    techStack: ["Google Gen AI SDK", "Function Calling", "ChromaDB / Pinecone", "Node.js"],
    architecturalApproach: "Structure agent interactions with explicit schema contracts, human-in-the-loop approvals, and comprehensive telemetry."
  },
  {
    id: 12,
    number: "12",
    title: "Workflow Automation",
    category: "AI & Automation",
    icon: Workflow,
    desc: "Connecting disparate third-party services into seamless webhook pipelines, automated data transformations, and transactional alerts.",
    impact: "Zero Hand-Off Lag",
    deliverables: ["n8n & Zapier Orchestrations", "Custom Webhook Receivers", "Automated Slack/Email Alerts", "ERP/CRM Data Sync"],
    techStack: ["n8n", "Webhooks", "Serverless Functions", "REST APIs"],
    architecturalApproach: "Implement idempotent retry logic and dead-letter queues so external API outages never drop customer transactions."
  },
  {
    id: 13,
    number: "13",
    title: "Design Systems",
    category: "Design & UX",
    icon: Grid,
    desc: "Creating component libraries with strict WCAG AA accessibility, standardized token architectures, and automated component documentation.",
    impact: "Rapid Product Iteration",
    deliverables: ["Accessible UI Primitives", "Design Token Hierarchies", "Storybook Component Catalogs", "Multi-Theme Dark/Light Support"],
    techStack: ["Tailwind CSS", "Radix UI", "Accessibility (a11y)", "TypeScript"],
    architecturalApproach: "Build headless accessible primitives wrapped with strict design tokens, enabling engineers to ship compliant UI at speed."
  },
  {
    id: 14,
    number: "14",
    title: "Performance & SEO Tuning",
    category: "Engineering",
    icon: Zap,
    desc: "Deep code, Core Web Vitals, and technical SEO optimization guaranteeing sub-second page loads and top organic search positioning.",
    impact: "Sub-Second Latency & Rank",
    deliverables: ["Bundle Size Reductions", "Edge Caching & CDN Tuning", "Technical Schema & Sitemaps", "Database Query Profiling"],
    techStack: ["Vite", "Lighthouse", "Edge Workers", "Schema.org", "Core Web Vitals"],
    architecturalApproach: "Profile before optimizing: eliminate render-blocking scripts, leverage code-splitting, and streamline critical rendering paths."
  },
  {
    id: 15,
    number: "15",
    title: "Admin Dashboards & Ops Portals",
    category: "Engineering",
    icon: LayoutDashboard,
    desc: "Designing and building high-density internal tools, operational command centers, live analytics dashboards, and multi-tenant management consoles.",
    impact: "Total Operational Visibility",
    deliverables: ["Real-Time Telemetry Dashboards", "Role-Based Staff Consoles", "Inventory & Order Control Hubs", "Automated Business Reporting"],
    techStack: ["React 19", "Tailwind CSS", "Recharts", "Supabase / PostgreSQL", "WebSockets"],
    architecturalApproach: "Structure dashboard components around fast event streams, server-side data aggregation, and zero-latency caching for real-time executive decision making."
  },
  {
    id: 16,
    number: "16",
    title: "Secure Login Portals & IAM",
    category: "Engineering",
    icon: Lock,
    desc: "Engineering frictionless, secure authentication gateways, enterprise SSO, multi-tenant customer onboarding portals, and passwordless authentication.",
    impact: "Frictionless, Secure Onboarding",
    deliverables: ["Multi-Tenant Client Portals", "Enterprise SSO & OAuth 2.0 / SAML", "Biometric & MFA Integration", "Self-Service User Provisioning"],
    techStack: ["Firebase Auth", "NextAuth / Auth.js", "OAuth 2.0", "WebAuthn / Passkeys", "JWT"],
    architecturalApproach: "Enforce strict zero-trust session management, encrypted cookie tokens, brute-force protection, and audit trails across all login flows."
  },
  {
    id: 17,
    number: "17",
    title: "Web & Mobile Apps",
    category: "Engineering",
    icon: Smartphone,
    desc: "End-to-end engineering of fluid cross-platform mobile apps (iOS & Android) and responsive progressive web applications built for high retention.",
    impact: "Cross-Platform Scalability",
    deliverables: ["React Native iOS & Android Apps", "Progressive Web Apps (PWA)", "Offline-First Synchronization", "Automated App Store & Play Store CI/CD"],
    techStack: ["React Native", "Expo", "TypeScript", "Tailwind / NativeWind", "SQLite Offline Storage"],
    architecturalApproach: "Leverage unified design tokens and platform-native gestures to deliver 60fps animations, instant offline responsiveness, and predictable state synchronization."
  },
  {
    id: 18,
    number: "18",
    title: "Enterprise Security & RBAC",
    category: "Engineering",
    icon: ShieldCheck,
    desc: "Hardening applications with zero-trust architecture, fine-grained role-based access control, cryptographic session handling, and audit-ready data compliance.",
    impact: "Audit-Ready Zero-Trust Defense",
    deliverables: ["Granular RBAC & Attribute-Based Access", "Zero-Trust API Hardening", "Data Encryption at Rest & In-Transit", "Automated Security Audit Logging"],
    techStack: ["OWASP Standards", "AES-256 Encryption", "PostgreSQL RLS", "Cloudflare WAF", "JWT & TLS 1.3"],
    architecturalApproach: "Enforce security at the database and API gateway layers via row-level security (RLS) and strict schema validation, ensuring zero unauthorized data leaks."
  }
];

const categories = ['All', 'Engineering', 'AI & Automation', 'Design & UX', 'Strategy & Growth'] as const;
type CategoryFilter = typeof categories[number];

export const Capabilities: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCapability, setActiveCapability] = useState<Capability | null>(null);

  useEffect(() => {
    if (activeCapability) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeCapability]);

  const filteredCapabilities = useMemo(() => {
    return capabilitiesData.filter(item => {
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch = !query || 
        item.title.toLowerCase().includes(query) ||
        item.desc.toLowerCase().includes(query) ||
        item.techStack.some(tech => tech.toLowerCase().includes(query)) ||
        item.deliverables.some(del => del.toLowerCase().includes(query));
      
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <div className="relative w-full bg-black text-white py-24 md:py-32 px-4 sm:px-6 md:px-12 border-t border-white/5 overflow-hidden">
      {/* Background Decorative Accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-[radial-gradient(ellipse_at_top,rgba(216,170,104,0.08),transparent_70%)] pointer-events-none -z-10" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,69,19,0.08),transparent_70%)] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-14 sm:mb-20">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 rounded-full bg-[#d8aa68] animate-pulse shadow-[0_0_8px_#d8aa68]" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#d8aa68]">
              What Am I Capable Of?
            </span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <h2 className="text-3xl sm:text-5xl md:text-6xl font-display font-bold tracking-tight text-white uppercase">
                Domain Agnostic Architecture <br className="hidden sm:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40">
                  & Execution
                </span>
              </h2>
              <p className="text-gray-400 text-base sm:text-lg max-w-2xl mt-4 font-sans leading-relaxed">
                A battle-tested spectrum across custom web &amp; mobile apps, high-power admin dashboards, authenticated login portals, enterprise security architectures, and automation disciplines. Structured to launch and scale resilient digital products without technical debt.
              </p>
            </div>

            {/* Quick Stats Summary */}
            <div className="flex items-center gap-4 sm:gap-6 bg-[#1a0c06]/40 border border-[#d8aa68]/20 rounded-2xl p-4 sm:p-5 backdrop-blur-sm self-start lg:self-auto shadow-[0_4px_20px_rgba(0,0,0,0.6)]">
              <div>
                <div className="text-2xl sm:text-3xl font-bold font-mono text-white">{capabilitiesData.length}</div>
                <div className="text-xs font-mono uppercase tracking-wider text-gray-500">Domains</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-2xl sm:text-3xl font-bold font-mono text-[#ffe0a3]">100%</div>
                <div className="text-xs font-mono uppercase tracking-wider text-gray-500">End-to-End</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-2xl sm:text-3xl font-bold font-mono text-[#d8aa68]">Full</div>
                <div className="text-xs font-mono uppercase tracking-wider text-gray-500">Production</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 mb-10 pb-6 border-b border-white/10">
          {/* Category Tabs - No scroll, naturally wrapping */}
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => {
              const count = cat === 'All' 
                ? capabilitiesData.length 
                : capabilitiesData.filter(c => c.category === cat).length;
              const isActive = selectedCategory === cat;

              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-mono tracking-wider uppercase whitespace-nowrap transition-all duration-200 flex items-center gap-2 border ${
                    isActive
                      ? 'bg-gradient-to-b from-[#4d2714] via-[#381b0e] to-[#140803] text-[#ffe0a3] font-bold border-[#d8aa68]/60 shadow-[0_4px_14px_rgba(0,0,0,0.8)]'
                      : 'bg-white/[0.04] text-gray-400 border-white/10 hover:border-[#d8aa68]/40 hover:text-white'
                  }`}
                >
                  <span>{cat}</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    isActive ? 'bg-[#d8aa68]/20 text-[#ffe0a3] border border-[#d8aa68]/30' : 'bg-white/10 text-gray-400'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Quick Search */}
          <div className="relative min-w-[240px] sm:w-72 shrink-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search skill, stack, or deliverable..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-10 pr-8 py-2 text-xs font-mono text-white placeholder-gray-500 focus:outline-none focus:border-[#d8aa68]/80 transition-colors"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Capabilities Grid - Clean, Non-overlapping, Completely Accessible */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCapabilities.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.04 }}
                onClick={() => setActiveCapability(item)}
                className="group relative rounded-2xl p-6 sm:p-7 flex flex-col justify-between transition-all duration-300 border border-[#d8aa68]/25 hover:border-[#d8aa68]/70 hover:shadow-[0_12px_36px_rgba(0,0,0,0.9),0_0_20px_rgba(216,170,104,0.15)] cursor-pointer overflow-hidden"
                style={{
                  background: 'linear-gradient(180deg, #1c0e07 0%, #130904 45%, #0d0502 100%)',
                }}
              >
                {/* Subtle Leather Stitch Accent Line */}
                <div className="absolute inset-[3px] rounded-[13px] border border-dashed border-[#d8aa68]/20 pointer-events-none group-hover:border-[#d8aa68]/40 transition-colors" />

                {/* Subtle Hover Gradient Accent */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#d8aa68]/[0.08] via-transparent to-[#4d2714]/[0.1] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                <div className="relative z-10">
                  {/* Top Bar: Icon, Number, Category Badge */}
                  <div className="flex items-center justify-between mb-5">
                    <div 
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center text-[#ffe0a3] border border-[#d8aa68]/60 shadow-[0_4px_14px_rgba(0,0,0,0.8),inset_0_1px_2px_rgba(255,255,255,0.2)] group-hover:scale-105 group-hover:border-[#ffe0a3] transition-all duration-300"
                      style={{
                        background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 45%, #241108 80%, #140803 100%)'
                      }}
                    >
                      <Icon size={20} strokeWidth={2} className="text-[#ffd885]" />
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="text-[11px] font-mono text-[#e5bb73] bg-[#2a1308]/80 border border-[#d8aa68]/30 px-2.5 py-0.5 rounded-full font-semibold">
                        {item.category}
                      </span>
                      <span className="font-mono text-xs text-[#d8aa68]/60 font-bold group-hover:text-[#ffd885] transition-colors">
                        {item.number}
                      </span>
                    </div>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold font-display text-white mb-2.5 tracking-tight group-hover:text-[#ffe0a3] transition-colors flex items-center justify-between">
                    <span>{item.title}</span>
                    <ArrowUpRight className="w-4 h-4 text-gray-500 group-hover:text-[#ffd885] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6 font-sans">
                    {item.desc}
                  </p>

                  {/* Deliverables Pills */}
                  <div className="mb-6 space-y-2">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-[#d8aa68] flex items-center gap-1.5 font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#ffd885]" />
                      Key Deliverables
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {item.deliverables.slice(0, 3).map((del, dIdx) => (
                        <span 
                          key={dIdx} 
                          className="text-[11px] font-mono px-2.5 py-1 rounded-md bg-[#241006]/70 border border-[#d8aa68]/20 text-gray-200"
                        >
                          {del}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer: Tech Stack & Impact Badge */}
                <div className="pt-4 border-t border-[#d8aa68]/15 flex items-center justify-between text-xs relative z-10">
                  <div className="flex items-center gap-1.5 text-gray-300 font-mono text-[11px]">
                    <Zap className="w-3.5 h-3.5 text-[#ffd885]" />
                    <span>{item.impact}</span>
                  </div>
                  <span className="font-mono text-[11px] text-[#ffd885] font-bold group-hover:underline">
                    View Blueprint &rarr;
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Empty Search State */}
        {filteredCapabilities.length === 0 && (
          <div className="py-20 text-center border border-dashed border-white/10 rounded-2xl bg-white/[0.01]">
            <Filter className="w-8 h-8 text-gray-600 mx-auto mb-3" />
            <h4 className="text-lg font-bold text-white mb-1">No matching capabilities found</h4>
            <p className="text-gray-400 text-sm mb-4">Try adjusting your keyword search or category filter.</p>
            <button
              onClick={() => {
                setSelectedCategory('All');
                setSearchQuery('');
              }}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-xs font-mono uppercase tracking-wider transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Detailed Technical Blueprint Drawer (Modal) using React Portal for proper fixed positioning */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {activeCapability && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-10">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setActiveCapability(null)}
                className="absolute inset-0 bg-black/80 backdrop-blur-md"
              />

              {/* Modal Dialog */}
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 20 }}
                transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                className="relative w-full max-w-2xl border border-[#d8aa68]/40 rounded-3xl p-6 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.95)] z-10 max-h-[90vh] overflow-y-auto"
                style={{
                  background: 'linear-gradient(180deg, #1f0f08 0%, #140904 50%, #0c0502 100%)',
                }}
              >
                {/* Close Button */}
                <button
                  onClick={() => setActiveCapability(null)}
                  className="absolute top-5 right-5 sm:top-6 sm:right-6 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#381b0e]/70 hover:bg-[#4d2714] border border-[#d8aa68]/40 flex items-center justify-center text-[#ffe0a3] hover:text-white transition-all shadow-md active:scale-95"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>

                {/* Modal Header */}
                <div className="flex items-center gap-4 mb-6 pr-10">
                  <div 
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-[#ffe0a3] border border-[#d8aa68]/70 shadow-[0_4px_16px_rgba(0,0,0,0.8),inset_0_1px_2px_rgba(255,255,255,0.25)] shrink-0"
                    style={{
                      background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 45%, #241108 80%, #140803 100%)'
                    }}
                  >
                    {React.createElement(activeCapability.icon, { size: 26, strokeWidth: 2, className: "text-[#ffd885]" })}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-[#ffd885] uppercase tracking-widest font-semibold">
                        {activeCapability.category}
                      </span>
                      <span className="text-gray-600 font-mono text-xs">•</span>
                      <span className="font-mono text-xs text-[#e5bb73] uppercase tracking-wider">
                        {activeCapability.impact}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold font-display text-white tracking-tight">
                      {activeCapability.title}
                    </h3>
                  </div>
                </div>

                {/* Core Description */}
                <p className="text-gray-300 text-base leading-relaxed mb-8 font-sans">
                  {activeCapability.desc}
                </p>

                {/* Architectural Approach */}
                <div className="bg-[#1a0c06]/50 border border-[#d8aa68]/20 rounded-2xl p-5 mb-8">
                  <div className="text-xs font-mono uppercase tracking-wider text-[#d8aa68] mb-2 font-semibold flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d8aa68]" />
                    Architectural Methodology
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {activeCapability.architecturalApproach}
                  </p>
                </div>

                {/* Deliverables Checklist */}
                <div className="mb-8">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-3.5">
                    Shipped Deliverables & Artifacts
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {activeCapability.deliverables.map((deliv, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-start gap-2.5 p-3 rounded-xl bg-white/[0.02] border border-white/5 text-sm text-gray-200"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#d8aa68] shrink-0 mt-0.5" />
                        <span>{deliv}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tech Stack & Frameworks */}
                <div className="mb-8">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-3">
                    Core Technologies & Frameworks
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {activeCapability.techStack.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-lg bg-[#381b0e]/40 border border-[#d8aa68]/30 text-[#ffe0a3] font-mono text-xs"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Close / Action */}
                <div className="pt-6 border-t border-white/10 flex justify-end gap-3">
                  <button
                    onClick={() => setActiveCapability(null)}
                    className="relative px-7 py-2.5 rounded-full overflow-hidden transition-all duration-300 group shadow-[0_4px_14px_rgba(0,0,0,0.8),inset_0_1px_1px_rgba(255,255,255,0.25)] border border-[#d8aa68]/50 hover:border-[#ffe0a3]/80"
                    style={{
                      background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 38%, #241108 72%, #140803 100%)'
                    }}
                  >
                    <span className="absolute inset-[2px] rounded-full border border-dashed border-[#d8aa68]/30 pointer-events-none group-hover:border-[#ffe0a3]/60 transition-colors"></span>
                    <span className="relative z-10 text-xs font-mono uppercase tracking-widest font-bold text-[#ffe0a3] group-hover:text-white transition-colors">
                      Done
                    </span>
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};
